import { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import useHttp from '@/shared/hooks/useHttp'
import { selectAuthData } from '@/containers/Login/AuthSelectors'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'baseui/snackbar'
import { AVAILABLE_COMMUNICATION_BOARD_SECTIONS } from '@/containers/SubscriptionFormPageNew/Components/CommunicationBoard/constants'

const useCommunicationBoard = urls => {
  const { tinymceKey, country } = useSelector(selectAuthData)
  const { sendRequest, getConfig } = useHttp()
  const { t } = useTranslation()
  const [section, setSection] = useState('supplier_status')
  const [content, setContent] = useState('')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const editorRef = useRef(null)
  const { enqueue } = useSnackbar()

  const sectionsList = AVAILABLE_COMMUNICATION_BOARD_SECTIONS.map(sectionKey => ({
    id: sectionKey,
    label: t(`devTools:communication_board.${sectionKey}`, sectionKey),
  }))

  useEffect(() => {
    if (!urls.communication_board_show || !section || !country) return
    setLoading(true)
    setError(null)
    sendRequest(
      `${urls.communication_board_show}?country=${country}&section=${section}&lang=${country}`,
      getConfig('GET'),
      data => {
        setContent(data?.content || '')
        setLoading(false)
      },
      () => {
        setLoading(false)
        setError(t('common:fetching_error'))
      },
    )
  }, [urls, country, section])

  const handleSectionChange = id => {
    setSection(id)
    setContent('')
    setError(null)
  }

  const handleContentChange = value => {
    setContent(value)
    setError(null)
  }

  const handleSave = onSuccess => {
    if (!urls.communication_board_update) return
    setSaving(true)
    sendRequest(
      `${urls.communication_board_update}?lang=${country}`,
      {
        ...getConfig('POST'),
        headers: {
          ...getConfig('POST').headers,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          country,
          section,
          content: editorRef.current?.getContent() || '',
        }),
      },
      () => {
        setSaving(false)
        enqueue({ message: t('common:saved'), kind: 'positive', autoHideDuration: 3000 })
        onSuccess && onSuccess()
      },
      () => {
        setSaving(false)
        enqueue({ message: t('common:error'), kind: 'negative', autoHideDuration: 4000 })
        setError(t('common:error'))
      },
    )
  }

  return {
    t,
    tinymceKey,
    country,
    section,
    content,
    sectionsList,
    loading,
    saving,
    error,
    handleSave,
    handleSectionChange,
    handleContentChange,
    editorRef,
  }
}

export default useCommunicationBoard
